import { useEffect, useRef, useState } from 'react';
import { useAlertsStore } from '../store/alerts';

/**
 * HUD Target Lock overlay — fires when a new conversion arrives.
 *
 * Visual sequence (~600ms total):
 *   1. Four corner brackets (⌜ ⌝ ⌞ ⌟) materialize OUTSIDE the target bbox
 *      and converge inward to the actual corners (0-300ms)
 *   2. Brief hold while locked (300-450ms)
 *   3. Brackets fade out (450-600ms)
 *
 * Target = topmost on-screen element with [data-flash-sub="<sub_id>"].
 * If no such element exists (e.g. user is on a page that doesn't render
 * sub_ids), the lock fires at center-of-viewport as a fallback.
 *
 * Replaces the old full-screen white flash + jagged yellow bolt — that
 * effect was too "natural phenomenon" (Thor / weather), this one is
 * "system response" (sniper scope / Predator HUD), which is closer to
 * the cyberpunk aesthetic of the rest of the dashboard.
 *
 * The component name + data-attr stay the same so existing imports across
 * the codebase keep working — no file rename / API churn.
 */

interface Lock {
  id: string;
  /** Target bbox relative to viewport, in CSS pixels. */
  rect: { left: number; top: number; width: number; height: number };
}

const LOCK_LIFETIME_MS = 650;
// Padding around the bbox so brackets don't visually crowd the text.
const BRACKET_INSET = -4;
// How big each bracket is (border-thickness × bracket-arm-length).
const BRACKET_SIZE = 14;
// How far brackets start from their final position (animation amplitude).
const CONVERGE_OFFSET = 22;

export function LightningStrikeOverlay() {
  const recent = useAlertsStore((s) => s.recentlyConverted);
  const [locks, setLocks] = useState<Lock[]>([]);
  // Track which `(subId, expiresAt)` pairs we've already animated, so re-renders
  // don't re-trigger animations for the same flash event.
  const seenRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    // BUGFIX: when a Modal is open it locks body scroll (`overflow:hidden`)
    // and renders its own backdrop at z-50. Our overlay sits at z-[180] —
    // higher — so brackets bleed through as glowing strips at the top of
    // viewport (target was a row in the dimmed dashboard behind the modal).
    // User saw three bright slivers above the SubIdDetailModal and reported
    // "ada bug di atas". Cleanest mitigation: suppress lock animations
    // entirely while any modal is open — the user is focused on modal
    // content, and the toast (z-[200]) still fires for awareness.
    if (typeof document !== 'undefined' && document.body.style.overflow === 'hidden') {
      // Mark recent flashes as seen so we don't replay them after modal closes.
      for (const flash of recent) {
        seenRef.current.add(`${flash.subId}:${flash.expiresAt}`);
      }
      return;
    }
    const newLocks: Lock[] = [];
    for (const flash of recent) {
      const key = `${flash.subId}:${flash.expiresAt}`;
      if (seenRef.current.has(key)) continue;
      seenRef.current.add(key);

      const cssEscaped = window.CSS?.escape
        ? window.CSS.escape(flash.subId)
        : flash.subId.replace(/["\\]/g, '\\$&');
      const els = document.querySelectorAll(`[data-flash-sub="${cssEscaped}"]`);

      // Find topmost on-screen target — striking every match is too noisy when
      // sub_id appears in many places (table row + sidebar + KPI card all at once).
      let bestRect: DOMRect | null = null;
      for (const el of els) {
        const rect = (el as HTMLElement).getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > window.innerHeight) continue; // off-screen
        if (!bestRect || rect.top < bestRect.top) bestRect = rect;
      }

      if (bestRect) {
        newLocks.push({
          id: `${flash.subId}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          rect: {
            left: bestRect.left,
            top: bestRect.top,
            width: bestRect.width,
            height: bestRect.height,
          },
        });
      } else {
        // Fallback: center-of-viewport phantom rect, ~120×24 (a typical sub_id chip)
        newLocks.push({
          id: `${flash.subId}-fallback-${Date.now()}`,
          rect: {
            left: window.innerWidth / 2 - 60,
            top: window.innerHeight / 2 - 12,
            width: 120,
            height: 24,
          },
        });
      }
    }

    if (newLocks.length === 0) return;
    setLocks((prev) => [...prev, ...newLocks]);

    // GC seenRef when it grows too large
    if (seenRef.current.size > 200) {
      const fresh = new Set<string>();
      for (const flash of recent) fresh.add(`${flash.subId}:${flash.expiresAt}`);
      seenRef.current = fresh;
    }
  }, [recent]);

  // Auto-clean locks after animation completes
  useEffect(() => {
    if (locks.length === 0) return;
    const ids = locks.map((s) => s.id);
    const t = setTimeout(() => {
      setLocks((prev) => prev.filter((s) => !ids.includes(s.id)));
    }, LOCK_LIFETIME_MS);
    return () => clearTimeout(t);
  }, [locks]);

  if (locks.length === 0) return null;

  return (
    <div className="fixed inset-0 pointer-events-none z-[180] overflow-hidden">
      {locks.map((l) => (
        <TargetLock key={l.id} lock={l} />
      ))}
    </div>
  );
}

// ─── Subcomponent ────────────────────────────────────────────────────────

function TargetLock({ lock }: { lock: Lock }) {
  // Outer box that contains the four brackets. We position each bracket
  // absolutely within this box at the corners, then animate them with
  // CSS keyframe `target-bracket-lock` which reads --tx/--ty per corner
  // for direction-of-convergence.
  const left = lock.rect.left + BRACKET_INSET;
  const top = lock.rect.top + BRACKET_INSET;
  const width = lock.rect.width - BRACKET_INSET * 2;
  const height = lock.rect.height - BRACKET_INSET * 2;

  // Border style for each corner — only the two relevant edges drawn.
  const borderColor = 'rgba(0, 255, 225, 0.95)';
  const borderWidth = 2;

  // Four brackets — top-left, top-right, bottom-left, bottom-right.
  // `tx`/`ty` set the starting offset (negative = outward from corresponding edge).
  const brackets: Array<{
    style: React.CSSProperties;
    tx: number;
    ty: number;
  }> = [
    {
      style: {
        left: 0,
        top: 0,
        borderLeft: `${borderWidth}px solid ${borderColor}`,
        borderTop: `${borderWidth}px solid ${borderColor}`,
      },
      tx: -CONVERGE_OFFSET,
      ty: -CONVERGE_OFFSET,
    },
    {
      style: {
        right: 0,
        top: 0,
        borderRight: `${borderWidth}px solid ${borderColor}`,
        borderTop: `${borderWidth}px solid ${borderColor}`,
      },
      tx: CONVERGE_OFFSET,
      ty: -CONVERGE_OFFSET,
    },
    {
      style: {
        left: 0,
        bottom: 0,
        borderLeft: `${borderWidth}px solid ${borderColor}`,
        borderBottom: `${borderWidth}px solid ${borderColor}`,
      },
      tx: -CONVERGE_OFFSET,
      ty: CONVERGE_OFFSET,
    },
    {
      style: {
        right: 0,
        bottom: 0,
        borderRight: `${borderWidth}px solid ${borderColor}`,
        borderBottom: `${borderWidth}px solid ${borderColor}`,
      },
      tx: CONVERGE_OFFSET,
      ty: CONVERGE_OFFSET,
    },
  ];

  return (
    <div className="absolute" style={{ left, top, width, height }}>
      {brackets.map((b, i) => (
        <div
          key={i}
          className="absolute"
          style={{
            width: BRACKET_SIZE,
            height: BRACKET_SIZE,
            opacity: 0,
            // CSS custom properties consumed by @keyframes target-bracket-lock
            ['--tx' as never]: `${b.tx}px`,
            ['--ty' as never]: `${b.ty}px`,
            animation: 'target-bracket-lock 0.6s cubic-bezier(0.22, 1, 0.36, 1) forwards',
            // Drop-shadow for the cyan glow halo without doubling DOM nodes.
            filter: 'drop-shadow(0 0 4px rgba(0, 255, 225, 0.7))',
            ...b.style,
          }}
        />
      ))}
    </div>
  );
}
